import * as Sentry from '@sentry/react';
import { datadogRum } from '@datadog/browser-rum';

type SeverityLevel = 'error' | 'warning' | 'info';

interface LogErrorOptions {
  level?: SeverityLevel;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

/**
 * Centralized error logger — sends to the console, Sentry and Datadog. All three should be
 * reached through this function so a provider can be swapped in one place.
 *
 * **`level` decides which Datadog stream this lands in, and that matters.** CAP's Datadog is
 * initialised RUM-only — no `datadogLogs.init`, no `forwardConsoleLogs` — so there is no log
 * stream to send a warning to, and `datadogRum.addError` has no severity of its own: anything
 * it receives is an Error. Sending every call there made a deliberate `level: 'warning'`
 * indistinguishable from a crash, and warnings outnumber errors here roughly eight to one, so
 * the Error stream was mostly not errors. A tester reading a RUM trace could not tell which
 * rows mattered.
 *
 * So non-error levels go to `addAction` instead, which is a first-class RUM event that does not
 * pollute the Error stream, and the level rides along as context either way. Sentry is
 * untouched: it already differentiates, and it keeps `captureException` for real Error objects
 * so stack traces survive.
 *
 * @param tag - Identifier for the error source (e.g., '[SmsVerify]')
 * @param message - Human-readable description
 * @param error - The error or value caught
 * @param extraOrOptions - Additional context, or full options object
 */
export const logError = (
  tag: string,
  message: string,
  error?: unknown,
  extraOrOptions?: Record<string, unknown> | LogErrorOptions,
): void => {
  const isOptions = extraOrOptions
    && ('level' in extraOrOptions || 'tags' in extraOrOptions);
  const opts: LogErrorOptions = isOptions
    ? extraOrOptions as LogErrorOptions
    : { extra: extraOrOptions as Record<string, unknown> | undefined };

  const { extra } = opts;
  const level = opts.level ?? 'error';
  const tags = { source: tag, ...opts.tags };
  const label = `${tag} ${message}`;

  // Match the console channel to the level too, so a warning is not red in devtools either.
  if (level === 'error') {
    console.error(label, error);
  } else if (level === 'warning') {
    console.warn(label, error);
  } else {
    console.info(label, error);
  }

  // Sentry, unchanged: captureException for real Errors keeps the stack trace, and
  // captureMessage already carries the level.
  if (error instanceof Error) {
    Sentry.captureException(error, { tags, extra: { message, ...extra } });
  } else {
    Sentry.captureMessage(label, {
      level,
      tags,
      extra: { error: String(error), ...extra },
    });
  }

  const context = error instanceof Error
    ? { ...tags, ...extra, level }
    : {
      ...tags, ...extra, originalError: String(error), level,
    };

  if (level !== 'error') {
    datadogRum.addAction(label, context);
    return;
  }

  datadogRum.addError(error instanceof Error ? error : new Error(label), context);
};
